// src/layouts/reports/ReportEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CheckCircle, Cancel, Send } from "@mui/icons-material"; // ➕ Bouton envoi unitaire
import { useParams } from "react-router-dom";
import api from "api/axios";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";

// ──────────────────────────────────────────────────────────────────────────────
// Couleurs & libellés de statut
const STATUS_COLORS = {
  NONE: "default",
  PENDING_SUP: "warning",
  PENDING_GA: "warning",
  PENDING_ED: "warning",
  APPROVED: "success",
  REJECTED: "error",
};
const STATUS_LABELS = {
  NONE: "Appliqué (aucune demande en attente)",
  PENDING_SUP: "En attente (Supervisor)",
  PENDING_GA: "En attente (Grants Admin)",
  PENDING_ED: "En attente (Executive Director)",
  APPROVED: "Approuvé",
  REJECTED: "Rejeté",
};

// Formatage affichage (FR)
function formatMoney(n, locale = "fr-FR", options = { maximumFractionDigits: 2 }) {
  const v = Number(n || 0);
  return v.toLocaleString(locale, options);
}

// ✅ Parse robuste des nombres saisis en FR (espaces fines/insécables + virgule)
function parseLocaleNumber(input) {
  if (input === "" || input === null || input === undefined) return null;
  let s = String(input).trim();
  s = s.replace(/\u00A0|\u202F/g, ""); // NBSP + NNBSP
  s = s.replace(/\s+/g, ""); // autres espaces
  s = s.replace(/,/g, "."); // virgule -> point
  // si plusieurs points, on garde le dernier comme décimal
  const parts = s.split(".");
  if (parts.length > 2) {
    const dec = parts.pop();
    s = parts.join("") + "." + dec;
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// Pastille de valeur
function valueChip(value, status = "NONE", { outlined = true, title } = {}) {
  return (
    <Chip
      size="small"
      label={formatMoney(value)}
      color={STATUS_COLORS[status] || "default"}
      variant={outlined ? "outlined" : "filled"}
      title={title || STATUS_LABELS[status] || ""}
    />
  );
}

const LEVELS = { PD: 1, SUP: 2, GA: 3, ED: 4 };

const deriveLevelFromUser = (u) => {
  if (!u) return null;
  if (u.is_founder) return "ED";
  const name = (u.global_role?.name || u.role || u.role_level || "").toLowerCase();
  if (name.includes("executive")) return "ED";
  if (name.includes("grants")) return "GA";
  if (name.includes("supervis")) return "SUP";
  if (name.includes("programme") || name.includes("program")) return "PD";
  return null;
};

// ✅ ED peut approuver (ou appliquer) les paliers inférieurs (>=)
const canApproveFor = (status, myLevel) => {
  if (!status || !myLevel) return false;
  const required = { PENDING_SUP: "SUP", PENDING_GA: "GA", PENDING_ED: "ED" }[status];
  if (!required) return false;
  return (LEVELS[myLevel] || 0) >= (LEVELS[required] || 0);
};

const ReportEditor = () => {
  const { reportId } = useParams();

  const [me, setMe] = useState(null);
  const [myLevel, setMyLevel] = useState(null);

  const [reportMeta, setReportMeta] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [rowMap, setRowMap] = useState({}); // account_name -> {projection, ...}
  const [edited, setEdited] = useState({}); // account_name -> { projection_input, modification_input }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // désactive Approve/Reject/Send pendant l’appel – clé = `${account}::${column}` (et `send::${key}`)
  const [working, setWorking] = useState({});

  const isED = useMemo(() => myLevel === "ED", [myLevel]);

  // ──────────────────────────────────────────────────────────────────────────
  // Chargement initial (profil, meta, données QB, puis /rows custom)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [meRes, metaRes] = await Promise.all([
          api.get("/users/me/").catch(() => ({ data: null })),
          api.get(`/quickbooks/custom-reports/${reportId}/`),
        ]);
        setMe(meRes.data || null);
        setMyLevel(deriveLevelFromUser(meRes.data));
        setReportMeta(metaRes.data);

        const fiscalYear = metaRes.data?.filters?.fiscal_year || "2025";
        const queryParams = new URLSearchParams({
          fiscal_year: fiscalYear,
          report_id: reportId,
        });

        const dataRes = await api.get(`/quickbooks/reports/budget-vs-actuals/?${queryParams}`);
        const qbRows = (dataRes.data?.data || []).sort((a, b) =>
          a.AccountName.localeCompare(b.AccountName)
        );
        setReportData(qbRows);

        await reloadRows(qbRows); // charge les rows custom (projection/modif/pending/statut)
      } catch (err) {
        console.error("Erreur chargement report :", err);
        setSnackbar({ open: true, message: "Erreur chargement du rapport", severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // ──────────────────────────────────────────────────────────────────────────
  // Recharge l’état custom (/rows/)
  const reloadRows = async (qbRowsFromState) => {
    const qbRows = qbRowsFromState || reportData;
    const rowsRes = await api.get(`/quickbooks/custom-reports/${reportId}/rows/`);
    const map = {};
    const initialEdited = { ...edited };

    (rowsRes.data || []).forEach((r) => {
      const acct = r.account_name || r.account || r.AccountName;
      if (!acct) return;
      map[acct] = {
        projection: r.projection ?? 0,
        modification: r.modification ?? 0,
        projection_pending: r.projection_pending ?? null,
        modification_pending: r.modification_pending ?? null,
        projection_status: r.projection_status || "NONE",
        modification_status: r.modification_status || "NONE",
      };
      // L’input montre la pending si elle existe, sinon la valeur appliquée
      initialEdited[acct] = {
        projection_input:
          r.projection_pending !== null ? String(r.projection_pending) : String(r.projection ?? ""),
        modification_input:
          r.modification_pending !== null
            ? String(r.modification_pending)
            : String(r.modification ?? ""),
      };
    });

    // Comptes sans ligne custom → initialise
    qbRows.forEach((r) => {
      const acct = r.AccountName;
      if (!map[acct]) {
        map[acct] = {
          projection: 0,
          modification: 0,
          projection_pending: null,
          modification_pending: null,
          projection_status: "NONE",
          modification_status: "NONE",
        };
        if (!initialEdited[acct])
          initialEdited[acct] = { projection_input: "", modification_input: "" };
      }
    });

    setRowMap(map);
    setEdited(initialEdited);
  };

  const onChange = (acct, field, value) => {
    setEdited((prev) => ({
      ...prev,
      [acct]: {
        ...prev[acct],
        [field]: value,
      },
    }));
  };

  // ──────────────────────────────────────────────────────────────────────────
  // ➤ Envoi ciblé : PROPOSER/APPLIQUER UNE SEULE CELLULE
  // Utilise le même endpoint batch avec un tableau de 1 élément.
  // ED : si input vide/invalid mais pending existe → applique la pending.
  const proposeOne = async (acct, column) => {
    const sendKey = `send::${acct}::${column}`;
    try {
      setWorking((w) => ({ ...w, [sendKey]: true }));

      const e = edited[acct] || {};
      const m = rowMap[acct] || {};
      const rawInput =
        column === "projection" ? (e.projection_input ?? "") : (e.modification_input ?? "");
      let val = parseLocaleNumber(rawInput);

      if (isED && (val === null || !Number.isFinite(val))) {
        const pending = column === "projection" ? m.projection_pending : m.modification_pending;
        if (pending !== null && pending !== undefined) {
          val = pending;
        }
      }

      if (val === null || !Number.isFinite(val)) {
        setSnackbar({ open: true, message: "Valeur invalide", severity: "warning" });
        return;
      }

      const resp = await api.post("/quickbooks/workflow/propose/", {
        report_id: Number(reportId),
        proposals: [{ account_name: acct, column, value: val, comment: "" }],
      });

      const ok = Array.isArray(resp?.data?.results) ? resp.data.results[0]?.ok === true : true;

      if (ok) {
        await reloadRows();
        setSnackbar({
          open: true,
          message: isED ? "Valeur appliquée" : "Proposition envoyée",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: resp?.data?.results?.[0]?.error || "Échec de l’envoi",
          severity: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Erreur d’envoi", severity: "error" });
    } finally {
      setWorking((w) => ({ ...w, [sendKey]: false }));
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // ➤ PROPOSER/APPLIQUER (BATCH global)
  //
  // ⚠️ CLÉ pour ED :
  // - si ED et qu’une valeur est en pending mais que l’input est vide (ou identique),
  //   on envoie QUAND MÊME la pending pour forcer l’application et nettoyer la pending côté serveur.
  // - si ED a saisi une valeur et qu’il existe une pending, on envoie la valeur saisie même si
  //   elle égale l’appliquée (nettoyage pending).
  // - autres niveaux : n’envoyer que si la saisie diffère de (pending ?? appliquée).
  //
  // Retourne {proposalsCount, acceptedCount}.
  const proposeBatch = async () => {
    const proposals = [];
    const invalidAccounts = [];

    // 🔁 Itérer sur rowMap (inclut les lignes avec pending même si input inchangé)
    for (const acct of Object.keys(rowMap)) {
      const e = edited[acct] || { projection_input: "", modification_input: "" };
      const m = rowMap[acct] || {};

      // Valeurs courantes (appliquées) et pending
      const curP = m.projection ?? 0;
      const curM = m.modification ?? 0;
      const pendP = m.projection_pending;
      const pendM = m.modification_pending;

      // Saisies utilisateur (FR)
      const pVal = parseLocaleNumber(e.projection_input);
      const mVal = parseLocaleNumber(e.modification_input);

      // ── PROJECTION
      if (isED) {
        if (pVal !== null && Number.isFinite(pVal)) {
          if (pVal !== curP || pendP !== null) {
            proposals.push({ account_name: acct, column: "projection", value: pVal, comment: "" });
          }
        } else if (pendP !== null) {
          // ED : input vide/invalid → applique la pending existante
          proposals.push({ account_name: acct, column: "projection", value: pendP, comment: "" });
        }
      } else {
        if (pVal !== null && Number.isFinite(pVal)) {
          const baseline = pendP !== null ? pendP : curP;
          if (pVal !== baseline) {
            proposals.push({ account_name: acct, column: "projection", value: pVal, comment: "" });
          }
        } else if (e.projection_input !== "" && pVal === null) {
          invalidAccounts.push(`${acct} (Projection)`);
        }
      }

      // ── MODIFICATION
      if (isED) {
        if (mVal !== null && Number.isFinite(mVal)) {
          if (mVal !== curM || pendM !== null) {
            proposals.push({
              account_name: acct,
              column: "modification",
              value: mVal,
              comment: "",
            });
          }
        } else if (pendM !== null) {
          proposals.push({ account_name: acct, column: "modification", value: pendM, comment: "" });
        }
      } else {
        if (mVal !== null && Number.isFinite(mVal)) {
          const baseline = pendM !== null ? pendM : curM;
          if (mVal !== baseline) {
            proposals.push({
              account_name: acct,
              column: "modification",
              value: mVal,
              comment: "",
            });
          }
        } else if (e.modification_input !== "" && mVal === null) {
          invalidAccounts.push(`${acct} (Modification)`);
        }
      }
    }

    if (invalidAccounts.length) {
      // message court, on coupe à 5 erreurs pour éviter un toast trop long
      throw new Error(
        `Valeurs invalides : ${invalidAccounts.slice(0, 5).join(", ")}${invalidAccounts.length > 5 ? "…" : ""}`
      );
    }

    // Rien à envoyer → informer handleSave()
    if (!proposals.length) {
      return { proposalsCount: 0, acceptedCount: 0 };
    }

    // POST workflow/propose
    const resp = await api.post("/quickbooks/workflow/propose/", {
      report_id: Number(reportId),
      proposals,
    });

    // L’API peut renvoyer soit { results: [...] }, soit { success: true } (sans details)
    let acceptedCount = 0;
    const results = resp?.data?.results;

    if (Array.isArray(results) && results.length) {
      // Comptage strict : r.ok === true
      acceptedCount = results.filter((r) => r && r.ok === true).length;
    } else {
      // Pas de tableau "results" → on considère que tout est OK si 200
      acceptedCount = proposals.length;
    }

    // Recharge les rows une seule fois après l’envoi
    await reloadRows();

    return { proposalsCount: proposals.length, acceptedCount };
  };

  // ── Approve/Reject par ligne (avec spinner par cellule)
  const approveOne = async (acct, column) => {
    const key = `${acct}::${column}`;
    try {
      setWorking((w) => ({ ...w, [key]: true }));
      await api.post("/quickbooks/workflow/approve/", {
        report_id: Number(reportId),
        account_name: acct,
        column,
        comment: "",
      });
      await reloadRows();
      setSnackbar({ open: true, message: "Approuvé 👍", severity: "success" });
    } catch (err) {
      console.error("approveOne error", err);
      setSnackbar({ open: true, message: "Échec de l'approbation", severity: "error" });
    } finally {
      setWorking((w) => ({ ...w, [key]: false }));
    }
  };

  const rejectOne = async (acct, column) => {
    const key = `${acct}::${column}`;
    try {
      setWorking((w) => ({ ...w, [key]: true }));
      await api.post("/quickbooks/workflow/reject/", {
        report_id: Number(reportId),
        account_name: acct,
        column,
        reason: "",
      });
      await reloadRows();
      setSnackbar({ open: true, message: "Rejeté 👎", severity: "success" });
    } catch (err) {
      console.error("rejectOne error", err);
      setSnackbar({ open: true, message: "Échec du rejet", severity: "error" });
    } finally {
      setWorking((w) => ({ ...w, [key]: false }));
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Bouton principal : “Appliquer” (ED) / “Proposer” (autres)
  // Affiche des toasts HONNÊTES selon ce qui a réellement été accepté côté API.
  const handleSave = async () => {
    try {
      setSaving(true);
      const { proposalsCount, acceptedCount } = await proposeBatch();

      if (!proposalsCount) {
        setSnackbar({ open: true, message: "Aucun changement à appliquer", severity: "info" });
        return;
      }
      if (!acceptedCount) {
        setSnackbar({
          open: true,
          message: "Aucun changement enregistré (droits manquants ?)",
          severity: "warning",
        });
        return;
      }

      setSnackbar({
        open: true,
        message: isED ? "Valeurs appliquées" : "Propositions envoyées",
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err?.message || "Erreur enregistrement",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Colonnes
  const columns = [
    { Header: "Account", accessor: "account", align: "left" },
    { Header: "Actual", accessor: "actual", align: "right" },
    { Header: "Projection", accessor: "projection", align: "left" },
    { Header: "Projection Status", accessor: "projection_status", align: "left" },
    { Header: "Actual + Projection", accessor: "actual_plus_projection", align: "right" },
    { Header: "Budget", accessor: "budget", align: "right" },
    { Header: "Modification", accessor: "modification", align: "left" },
    { Header: "Modification Status", accessor: "modification_status", align: "left" },
    { Header: "Budget Update", accessor: "budget_update", align: "right" },
  ];

  // Lignes
  const rows = reportData.map((row) => {
    const account = row.AccountName;
    const actual = Number(row.TotalActual || 0);
    const budget = Number(row.TotalBudget || 0);

    const meta = rowMap[account] || {};
    const projInput = edited[account]?.projection_input ?? "";
    const modInput = edited[account]?.modification_input ?? "";

    const projStatus = meta.projection_status || "NONE";
    const modStatus = meta.modification_status || "NONE";

    const projection = Number(meta.projection || 0);
    const modification = Number(meta.modification || 0);
    const actualPlusProjection = actual + projection;
    const budgetUpdate = budget + modification;

    const canApproveProjection = canApproveFor(projStatus, myLevel);
    const canApproveModification = canApproveFor(modStatus, myLevel);

    const keyProj = `${account}::projection`;
    const keyMod = `${account}::modification`;
    const sendProjKey = `send::${keyProj}`;
    const sendModKey = `send::${keyMod}`;

    return {
      account,
      actual: formatMoney(actual),

      // Projection (saisie texte + bouton "envoyer cette cellule")
      projection: (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 300 }}>
          <TextField
            value={projInput}
            onChange={(e) => onChange(account, "projection_input", e.target.value)}
            type="text"
            inputMode="decimal"
            size="small"
            inputProps={{ style: { textAlign: "right" }, "aria-label": "Projection" }}
            sx={{ width: 140 }}
          />

          {/* ➕ Envoi unitaire de cette projection */}
          <Tooltip title="Envoyer uniquement cette projection">
            <span>
              <IconButton
                size="small"
                onClick={() => proposeOne(account, "projection")}
                disabled={!!working[sendProjKey]}
              >
                {working[sendProjKey] ? <CircularProgress size={16} /> : <Send fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={STATUS_LABELS[projStatus] || ""}>
            <Chip
              size="small"
              label={projStatus}
              color={STATUS_COLORS[projStatus] || "default"}
              variant={projStatus === "NONE" ? "outlined" : "filled"}
            />
          </Tooltip>

          {canApproveProjection && (
            <>
              <Tooltip title="Approve">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => approveOne(account, "projection")}
                    disabled={!!working[keyProj]}
                  >
                    {working[keyProj] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => rejectOne(account, "projection")}
                    disabled={!!working[keyProj]}
                  >
                    {working[keyProj] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Cancel color="error" fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
      ),

      // Projection Status (appliquée + pending si présente)
      projection_status: (
        <Stack direction="row" spacing={1} alignItems="center">
          {valueChip(projection, projStatus, {
            outlined: true,
            title: `Projection — ${STATUS_LABELS[projStatus] || ""}`,
          })}
          {meta.projection_pending !== null &&
            valueChip(Number(meta.projection_pending), projStatus, {
              outlined: false,
              title: "Projection — En attente",
            })}
        </Stack>
      ),

      actual_plus_projection: formatMoney(actualPlusProjection),
      budget: formatMoney(budget),

      // Modification (saisie texte + bouton "envoyer cette cellule")
      modification: (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 300 }}>
          <TextField
            value={modInput}
            onChange={(e) => onChange(account, "modification_input", e.target.value)}
            type="text"
            inputMode="decimal"
            size="small"
            inputProps={{ style: { textAlign: "right" }, "aria-label": "Modification" }}
            sx={{ width: 140 }}
          />

          {/* ➕ Envoi unitaire de cette modification */}
          <Tooltip title="Envoyer uniquement cette modification">
            <span>
              <IconButton
                size="small"
                onClick={() => proposeOne(account, "modification")}
                disabled={!!working[sendModKey]}
              >
                {working[sendModKey] ? <CircularProgress size={16} /> : <Send fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={STATUS_LABELS[modStatus] || ""}>
            <Chip
              size="small"
              label={modStatus}
              color={STATUS_COLORS[modStatus] || "default"}
              variant={modStatus === "NONE" ? "outlined" : "filled"}
            />
          </Tooltip>
          {canApproveModification && (
            <>
              <Tooltip title="Approve">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => approveOne(account, "modification")}
                    disabled={!!working[keyMod]}
                  >
                    {working[keyMod] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => rejectOne(account, "modification")}
                    disabled={!!working[keyMod]}
                  >
                    {working[keyMod] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Cancel color="error" fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
      ),

      // Modification Status (appliquée + pending si présente)
      modification_status: (
        <Stack direction="row" spacing={1} alignItems="center">
          {valueChip(modification, modStatus, {
            outlined: true,
            title: `Modification — ${STATUS_LABELS[modStatus] || ""}`,
          })}
          {meta.modification_pending !== null &&
            valueChip(Number(meta.modification_pending), modStatus, {
              outlined: false,
              title: "Modification — En attente",
            })}
        </Stack>
      ),

      // Budget Update
      budget_update:
        budget + modification < 0 ? (
          <Typography color="error" align="right" component="span">
            Erreur
          </Typography>
        ) : (
          formatMoney(budgetUpdate)
        ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4" gutterBottom>
                Modifier le rapport : {reportMeta?.name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {reportMeta?.description}
              </Typography>
              <div style={{ opacity: 0.7, marginTop: 4 }}>
                Your level: <b>{myLevel || "-"}</b>{" "}
                {isED
                  ? "(apply = auto-approve + applique les pendings)"
                  : "(save = propose; needs approval)"}
              </div>
            </Grid>
            <Grid item>
              <Button variant="contained" color="success" disabled={saving} onClick={handleSave}>
                {saving ? "Envoi..." : isED ? "Appliquer" : "Proposer"}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isLoading={false}
            noEndBorder
            entriesPerPage={false}
            pagination={{ enabled: false }}
            showTotalEntries={false}
            stickyHeader
            stickyColumn
            renderAllRows
          />
        )}
      </MDBox>
      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default ReportEditor;
